import { useCallback, useEffect, useState } from 'react';
import { Row } from '@tanstack/react-table';

import { useDrawer } from '@xemplo/drawer';
import { AmendmentListItem } from '@xemplo/gp-types';

import { PayrunAmendmentDetail } from '../payrun-amendment-detail';
import { PayrunAmendmentDetailFooter } from '../payrun-amendment-detail-footer';
import { PayrunAmendmentDetailHeader } from '../payrun-amendment-detail-header';

export const useAmendmentDetailDrawer = () => {
  const { setContent, toggle, setConfig, drawerState } = useDrawer({ isOpen: false });
  const [amendmentId, setAmendmentId] = useState<string | null>(null);
  const [activeRowId, setActiveRowId] = useState<string | null>(null);

  /** Reset Drawer to empty closed state */
  const resetDrawer = useCallback(() => {
    toggle(false);
    setActiveRowId(null);
    setAmendmentId(null);
  }, [toggle, setActiveRowId, setAmendmentId]);

  /** Handle opening and updating drawer details when amendment is selected
   * @param row - the row that was selected
   */
  const updateDrawer = (row: Row<AmendmentListItem>) => {
    //close drawer only.
    setConfig({
      onClose: () => {
        resetDrawer();
      },
    });
    // If the row is already active, toggle the drawer and reset the active row
    if (row.id === activeRowId) {
      setActiveRowId(null);
      toggle(false);
      return;
    }
    // Otherwise, set the row as active
    setActiveRowId(row.id);

    setAmendmentId(row.original.amendmentId);
    // If the drawer is already open, leave it open
    if (drawerState.isOpen) return;
    // Otherwise, open the drawer
    toggle();
  };

  //If the amendment changes, update the drawer
  useEffect(() => {
    setContent({
      header: amendmentId ? (
        <PayrunAmendmentDetailHeader amendmentId={amendmentId} />
      ) : null,
      body: amendmentId ? <PayrunAmendmentDetail amendmentId={amendmentId} /> : null,
      footer: amendmentId ? (
        <PayrunAmendmentDetailFooter amendmentId={amendmentId} onSuccess={resetDrawer} />
      ) : null,
    });
  }, [amendmentId, resetDrawer, setContent]);

  return {
    toggle,
    resetDrawer,
    setContent,
    setConfig,
    drawerState,
    updateDrawer,
  };
};
